import React, { FunctionComponent } from 'react'
import { InfoIcon } from '../Icons/Icons'
import { PH_LIGHT } from './phosphor'
import shell from './BriefShell.module.css'
import styles from './FleetValueFormula.module.css'

/*
 * How estimated fleet value is worked out, beside the Intel tab's value fields, in one of two styles (a demo
 * option): the formula as a sum (its three terms with icons, added up to the total), or a sentence.
 */

export type FleetValueFormulaStyle = 'formula' | 'text'

const LABEL = 'How estimated fleet value is calculated'
const HEADING = 'Contract value estimation'

/* devices: the printer icon; the pages: the brief's legend swatches for B&W and color */
const TermIcon: FunctionComponent<{ name: 'devices' | 'mono' | 'colour' }> = ({ name }) =>
  name === 'devices' ? (
    <svg viewBox='0 0 256 256' aria-hidden='true' className={styles.icon}>
      {PH_LIGHT.devices.map(p => (
        // the light icon, outlined in its own colour to thicken its strokes
        <path
          key={p.d.slice(0, 24)}
          d={p.d}
          fill='currentColor'
          stroke='currentColor'
          strokeWidth='8'
          strokeLinejoin='round'
        />
      ))}
    </svg>
  ) : (
    <span
      className={`${shell.swatch} ${name === 'mono' ? shell.swatchBW : shell.swatchColor} ${styles.swatch}`}
      aria-hidden='true'
    />
  )

/* each term: what's counted for the customer, times the rate set here */
const TERMS: { key: 'devices' | 'mono' | 'colour'; count: string; rate: string }[] = [
  { key: 'devices', count: 'Devices', rate: 'Average value per device' },
  { key: 'mono', count: 'Average monthly B&W pages', rate: 'Cost per B&W page' },
  { key: 'colour', count: 'Average monthly color pages', rate: 'Cost per color page' },
]

export const FleetValueFormula: FunctionComponent<{ variant: FleetValueFormulaStyle }> = ({ variant }) => {
  if (variant === 'text') {
    return (
      <div className={`${styles.box} ${styles.textBox}`} aria-label={LABEL}>
        <i className={`icon twelve ${styles.infoIcon}`} aria-hidden='true'>
          <InfoIcon />
        </i>
        <div>
          <h5 className={styles.heading}>{HEADING}</h5>
          <p>
            Used for estimating annual contract value. It&apos;s calculated as total devices multiplied by average value
            per device, plus average monthly page volume multiplied by cost per page.
          </p>
          <p>
            Adjust these settings based on your real-world knowledge, or remove page-based costs for device-only
            contract estimates.
          </p>
        </div>
      </div>
    )
  }

  // worked like a sum, reading on from row to row: each term plus the next, then the total under a rule
  return (
    <div className={`${styles.box} ${styles.sum}`} role='group' aria-label={LABEL}>
      <h5 className={`${styles.heading} ${styles.sumHeading}`}>{HEADING}</h5>
      {TERMS.map((term, i) => (
        <div key={term.key} className={styles.term}>
          <TermIcon name={term.key} />
          <span className={styles.count}>{term.count}</span>
          <span className={styles.times}>×</span>
          <span className={styles.rate}>{term.rate}</span>
          {/* reading on: plus the next row, and the last equals the total under it */}
          <span className={styles.operator}>{i < TERMS.length - 1 ? '+' : '='}</span>
        </div>
      ))}
      <div className={styles.total}>Estimated fleet value</div>
    </div>
  )
}
